import { Box, Button, Flex, Icon, Text } from '@chakra-ui/react';
import {
  ExternalIngestionDocumentFragment,
  ExternalValidationReportDataFragment,
  GetIngestionDocumentFragment,
  IngestionDocumentReviewFragment,
  IngestionDocumentValidationErrorReviewFragment,
} from '@client/graphql/__generated__/types';
import { FolderIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { ComplianceDocumentSidebarItem } from '../components/ComplianceDocumentSidebarItem';
import { UngroupedDocumentsSection } from '../components/UngroupedDocumentsSection';

interface FolderDocumentsSectionProps {
  folderName: string;
  activeDocuments: ExternalIngestionDocumentFragment[];
  archivedDocuments: ExternalIngestionDocumentFragment[];
  selectedDocument: GetIngestionDocumentFragment | null;
  validationData: ExternalValidationReportDataFragment | null;
  documentReviews?: IngestionDocumentReviewFragment[];
  submittedDocumentReviews?: IngestionDocumentReviewFragment[];
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[];
  defaultCollapsed?: boolean;
  onDocumentSelect: (
    document: ExternalIngestionDocumentFragment,
    contractDocumentId?: string
  ) => void;
}

export const FolderDocumentsSection: FC<FolderDocumentsSectionProps> = ({
  folderName,
  activeDocuments,
  archivedDocuments,
  selectedDocument,
  validationData,
  documentReviews,
  submittedDocumentReviews,
  hiddenDocumentValidationReviews,
  defaultCollapsed = true,
  onDocumentSelect,
}) => {
  const [showArchived, setShowArchived] = useState(false);

  const hasSelectedDocument =
    activeDocuments.some((doc) => doc.id === selectedDocument?.id) ||
    archivedDocuments.some((doc) => doc.id === selectedDocument?.id);

  const hasArchivedDocuments = archivedDocuments.length > 0;

  const toggleShowArchived = () => {
    setShowArchived(!showArchived);
  };

  const getTitle = (folderName: string) => (
    <Flex align="center" gap={1}>
      <Icon as={FolderIcon} />
      <Text>{folderName}</Text>
    </Flex>
  );

  return (
    <>
      {/* Active Documents Section */}
      {activeDocuments.length > 0 && (
        <UngroupedDocumentsSection
          isCollapsible
          defaultCollapsed={defaultCollapsed && !hasSelectedDocument}
          documentReviews={documentReviews}
          documents={activeDocuments}
          hiddenDocumentValidationReviews={hiddenDocumentValidationReviews}
          selectedDocument={selectedDocument}
          showSubtitle={true}
          submittedDocumentReviews={submittedDocumentReviews}
          subtitleType="status"
          title={getTitle(folderName)}
          validationData={validationData}
          onDocumentSelect={onDocumentSelect}
        >
          {/* Show Archived Button and Documents */}
          {hasArchivedDocuments && (
            <>
              <Button
                color="compliance.text.muted"
                fontSize="xs"
                fontWeight="normal"
                h="auto"
                justifyContent="flex-start"
                minH="auto"
                p={2}
                variant="link"
                onClick={toggleShowArchived}
              >
                {showArchived ? 'Hide' : 'Show'} Archived (
                {archivedDocuments.length})
              </Button>

              {showArchived &&
                archivedDocuments.map((document) => {
                  const isDocumentSelected =
                    selectedDocument?.id === document.id;

                  return (
                    <Box
                      key={document.id}
                      bg={
                        isDocumentSelected
                          ? 'compliance.selected.bg'
                          : undefined
                      }
                      borderRadius="md"
                      p={1}
                    >
                      <ComplianceDocumentSidebarItem
                        documentItem={{
                          document,
                          contractDocumentId:
                            document.contractDocuments?.[0]?.id,
                        }}
                        documentReviews={documentReviews}
                        hiddenDocumentValidationReviews={
                          hiddenDocumentValidationReviews
                        }
                        isSelected={isDocumentSelected}
                        shouldShowStatus={false}
                        showSubtitle={false}
                        submittedDocumentReviews={submittedDocumentReviews}
                        validationData={validationData}
                        onSelect={onDocumentSelect}
                      />
                    </Box>
                  );
                })}
            </>
          )}
        </UngroupedDocumentsSection>
      )}

      {/* Only Archived Documents Section (when no active documents) */}
      {activeDocuments.length === 0 && hasArchivedDocuments && (
        <UngroupedDocumentsSection
          isCollapsible
          defaultCollapsed={defaultCollapsed && !hasSelectedDocument}
          documentReviews={documentReviews}
          documents={archivedDocuments}
          hiddenDocumentValidationReviews={hiddenDocumentValidationReviews}
          selectedDocument={selectedDocument}
          shouldShowStatus={false}
          submittedDocumentReviews={submittedDocumentReviews}
          title={getTitle(`${folderName} - Archived`)}
          validationData={validationData}
          onDocumentSelect={onDocumentSelect}
        />
      )}

      {/* No Documents */}
      {activeDocuments.length === 0 && archivedDocuments.length === 0 && (
        <Text color="compliance.text.muted" fontSize="sm" p={2}>
          No documents in {folderName}
        </Text>
      )}
    </>
  );
};
